import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase-service';
import { RefreshCw, Search, ShieldAlert, Calendar, User, Database, Eye } from 'lucide-react';
import { cn } from '../SharedComponents';

export const AuditCardexPanel = ({ defaultSearch }: { defaultSearch?: string }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableExists, setTableExists] = useState(true);

  // Filters
  const [moduleFilter, setModuleFilter] = useState('ALL');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState(defaultSearch || '');

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: sbError } = await supabase.rpc('get_gov_audit_log_cardex');

      if (sbError) {
        if (sbError.code === '42P01') {
          // Table does not exist yet
          setTableExists(false);
        } else {
          setError(sbError.message);
        }
      } else {
        setLogs(data || []);
        setTableExists(true);
      }
    } catch (err: any) {
      setError(err.message || 'Error loading logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  if (!tableExists) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-slate-900 border border-slate-800 rounded-2xl text-center">
        <ShieldAlert size={48} className="text-slate-600 mb-4" />
        <h2 className="text-xl font-black text-white uppercase tracking-widest mb-2">Cardex No Disponible</h2>
        <p className="text-slate-400 text-sm max-w-md">
          El repositorio de auditoría (Cardex) aún no se encuentra activo en la base de datos o no hay eventos registrados. 
          Contacte al administrador para ejecutar la migración SQL correspondiente.
        </p>
      </div>
    );
  }

  const filteredLogs = logs.filter(log => {
    if (moduleFilter !== 'ALL' && log.module !== moduleFilter) return false;
    if (actionFilter !== 'ALL' && log.action !== actionFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        log.user_name?.toLowerCase().includes(q) ||
        log.user_email?.toLowerCase().includes(q) ||
        log.instance_name?.toLowerCase().includes(q) ||
        log.entity_type?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden flex flex-col h-[600px]">
      {/* Header & Filters */}
      <div className="p-4 border-b border-slate-800 bg-slate-950 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-4 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input 
              type="text" 
              placeholder="Buscar usuario o instancia..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-4 py-2 text-xs text-white focus:border-emerald-500/50 outline-none w-64"
            />
          </div>
          <select 
            value={moduleFilter} 
            onChange={e => setModuleFilter(e.target.value)}
            className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white outline-none"
          >
            <option value="ALL">Todos los módulos</option>
            <option value="CONFIGURACION">Configuración</option>
            <option value="CONTROL_OPERATIVO">Control Operativo</option>
            <option value="PLANEACION">Planeación</option>
            <option value="SEGUIMIENTO">Seguimiento</option>
            <option value="USUARIOS">Usuarios</option>
          </select>
          <select 
            value={actionFilter} 
            onChange={e => setActionFilter(e.target.value)}
            className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-xs text-white outline-none"
          >
            <option value="ALL">Todas las acciones</option>
            <option value="CREATE">Creación</option>
            <option value="UPDATE">Edición</option>
            <option value="DELETE">Eliminación</option>
            <option value="APPROVE">Aprobación</option>
          </select>
        </div>
        <button 
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold uppercase tracking-widest rounded-lg transition-colors"
        >
          <RefreshCw size={14} className={cn(loading && "animate-spin")} />
          Actualizar
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full text-slate-500 text-sm">Cargando eventos...</div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-rose-500 text-sm">{error}</div>
        ) : filteredLogs.length === 0 ? (
          <div className="flex items-center justify-center h-full text-slate-500 text-sm">No se encontraron registros.</div>
        ) : (
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-950 text-slate-500 uppercase tracking-widest sticky top-0">
              <tr>
                <th className="p-4 font-black">Fecha/Hora</th>
                <th className="p-4 font-black">Usuario</th>
                <th className="p-4 font-black">Módulo/Instancia</th>
                <th className="p-4 font-black">Acción</th>
                <th className="p-4 font-black">Entidad</th>
                <th className="p-4 font-black text-right">Detalle</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 text-slate-300">
              {filteredLogs.map(log => (
                <tr key={log.id} className="hover:bg-slate-800/50 transition-colors">
                  <td className="p-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Calendar size={12} className="text-slate-500" />
                      {new Date(log.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <User size={12} className="text-slate-500" />
                      <div className="flex flex-col">
                        <span className="font-bold text-white">{log.user_name || 'Sistema'}</span>
                        <span className="text-[10px] text-slate-500">{log.user_email || 'system@core'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Database size={12} className="text-slate-500" />
                      <div className="flex flex-col">
                        <span className="font-bold text-emerald-400">{log.module}</span>
                        <span className="text-[10px] text-slate-400 truncate max-w-[150px]">{log.instance_name || 'Global'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={cn(
                      "px-2 py-1 rounded text-[10px] font-black uppercase tracking-widest",
                      log.action === 'CREATE' ? "bg-emerald-500/20 text-emerald-400" :
                      log.action === 'UPDATE' ? "bg-amber-500/20 text-amber-400" :
                      log.action === 'DELETE' ? "bg-rose-500/20 text-rose-400" :
                      "bg-slate-500/20 text-slate-400"
                    )}>
                      {log.action}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex flex-col">
                      <span className="font-bold">{log.entity_type}</span>
                      {log.field_name && <span className="text-[10px] text-slate-500">Campo: {log.field_name}</span>}
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <button 
                      className="p-2 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
                      title="Ver detalle JSON"
                      onClick={() => alert(`Metadata: ${JSON.stringify(log.metadata)}\nSource: ${log.source || 'N/D'}\nVersion: ${log.app_version || 'N/D'}`)}
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
